import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { dashboardAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const {
  FiUsers, FiUserCheck, FiFileText, FiTrendingUp, FiCalendar,
  FiGift, FiAlertCircle, FiAlertTriangle, FiXCircle, FiUpload,
  FiUser, FiBook, FiMessageSquare, FiCheckCircle, FiClock,
} = FiIcons;

const ACTIVITY_ICONS = {
  user: { icon: FiUser, color: 'text-blue-500', bg: 'bg-blue-100' },
  policy: { icon: FiFileText, color: 'text-purple-500', bg: 'bg-purple-100' },
  coaching: { icon: FiMessageSquare, color: 'text-indigo-500', bg: 'bg-indigo-100' },
  import: { icon: FiUpload, color: 'text-green-500', bg: 'bg-green-100' },
  system: { icon: FiCheckCircle, color: 'text-gray-500', bg: 'bg-gray-100' },
};

const AgentRow = ({ agent, dateLabel, dateValue, statusColor = 'border-red-400' }) => (
  <div className={`flex items-center bg-white p-3 rounded-lg shadow-sm border-l-4 ${statusColor}`}>
    {agent.profileImage ? (
      <img src={agent.profileImage} alt="" className="w-8 h-8 rounded-full object-cover mr-3 flex-shrink-0" />
    ) : (
      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 mr-3 flex-shrink-0 text-xs font-bold">
        {agent.fullName?.charAt(0)}
      </div>
    )}
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-gray-900 truncate">{agent.fullName}</p>
      <div className="flex items-center space-x-2 text-xs text-gray-500 mt-0.5">
        <span>{agent.agentCode}</span>
        {agent.licenseNumber && agent.licenseNumber !== agent.agentCode && (
          <><span className="text-gray-300">·</span><span>ใบอนุญาต: {agent.licenseNumber}</span></>
        )}
      </div>
    </div>
    <div className="text-right ml-2 flex-shrink-0">
      <p className="text-xs text-gray-400">{dateLabel}</p>
      <p className="text-xs font-medium text-red-600">
        {dateValue ? new Date(dateValue).toLocaleDateString('th-TH', { day: '2-digit', month: 'short', year: '2-digit' }) : '-'}
      </p>
    </div>
  </div>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState({ expiredAgents: [], expiringSoon: [], terminatedAgents: [] });
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [events, setEvents] = useState({ birthdays: [], expiringLicenses: [] });

  useEffect(() => {
    Promise.all([
      dashboardAPI.getAdminDashboardStats(),
      dashboardAPI.getAdminAlerts(),
    ]).then(([statsRes, alertsRes]) => {
      setStats(statsRes.data);
      setAlerts(alertsRes.data);
    }).catch(console.error).finally(() => {
      setLoading(false);
      setAlertsLoading(false);
    });
  }, []);

  useEffect(() => {
    dashboardAPI.getAdminDashboardEvents(selectedMonth)
      .then(({ data }) => setEvents(data))
      .catch(console.error);
  }, [selectedMonth]);

  const months = [
    { value: 1, label: 'มกราคม' }, { value: 2, label: 'กุมภาพันธ์' },
    { value: 3, label: 'มีนาคม' }, { value: 4, label: 'เมษายน' },
    { value: 5, label: 'พฤษภาคม' }, { value: 6, label: 'มิถุนายน' },
    { value: 7, label: 'กรกฎาคม' }, { value: 8, label: 'สิงหาคม' },
    { value: 9, label: 'กันยายน' }, { value: 10, label: 'ตุลาคม' },
    { value: 11, label: 'พฤศจิกายน' }, { value: 12, label: 'ธันวาคม' },
  ];

  const formatCurrency = (value) =>
    new Intl.NumberFormat('th-TH', { style: 'currency', currency: 'THB', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);

  if (loading) return <LoadingSpinner />;
  if (!stats) return <div className="p-8 text-center text-gray-500">ไม่สามารถโหลดข้อมูลได้</div>;

  return (
    <div className="px-4 sm:px-6 lg:px-8 pb-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">Admin Dashboard</h1>
        <p className="text-gray-500 text-sm">ภาพรวมระบบและการจัดการ</p>
      </div>

      {/* ── Stats Grid ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center">
            <div className="p-2.5 bg-blue-50 rounded-lg mr-3">
              <SafeIcon icon={FiUsers} className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">ผู้ใช้ทั้งหมด</p>
              <p className="text-xl font-bold text-gray-900">{stats.totalUsers}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center">
            <div className="p-2.5 bg-green-50 rounded-lg mr-3">
              <SafeIcon icon={FiUserCheck} className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">ผู้ใช้ที่ active</p>
              <p className="text-xl font-bold text-gray-900">{stats.activeAgents}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center">
            <div className="p-2.5 bg-purple-50 rounded-lg mr-3">
              <SafeIcon icon={FiFileText} className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">กรมธรรม์ทั้งหมด</p>
              <p className="text-xl font-bold text-gray-900">{stats.totalPolicies}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center">
            <div className="p-2.5 bg-orange-50 rounded-lg mr-3">
              <SafeIcon icon={FiTrendingUp} className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">รายได้รวม</p>
              <p className="text-lg font-bold text-gray-900">{formatCurrency(stats.monthlyRevenue)}</p>
            </div>
          </div>
        </div>

        {/* License alert cards */}
        <div className="bg-red-50 rounded-xl shadow-sm border border-red-100 p-5">
          <div className="flex items-center">
            <div className="p-2.5 bg-red-100 rounded-lg mr-3">
              <SafeIcon icon={FiXCircle} className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-xs text-red-600 font-medium">ใบอนุญาตหมดอายุแล้ว</p>
              <p className="text-xl font-bold text-red-700">{stats.expiredLicensesCount ?? alerts.expiredAgents.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-orange-50 rounded-xl shadow-sm border border-orange-100 p-5">
          <div className="flex items-center">
            <div className="p-2.5 bg-orange-100 rounded-lg mr-3">
              <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-xs text-orange-600 font-medium">หมดอายุใน 30 วัน</p>
              <p className="text-xl font-bold text-orange-700">{stats.expiringSoonCount ?? alerts.expiringSoon.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Alert Sections ── */}
      {!alertsLoading && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Expired Licenses */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-50">
              <div className="flex items-center text-red-600">
                <SafeIcon icon={FiXCircle} className="w-4 h-4 mr-2" />
                <h3 className="font-semibold text-gray-900 text-sm">ใบอนุญาตหมดอายุแล้ว</h3>
              </div>
              <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-0.5 rounded-full">{alerts.expiredAgents.length}</span>
            </div>
            <div className="p-4 space-y-2 max-h-64 overflow-y-auto">
              {alerts.expiredAgents.length > 0 ? alerts.expiredAgents.map(a => (
                <AgentRow key={a.id} agent={a} dateLabel="หมดอายุ" dateValue={a.licenseExpiryDate} statusColor="border-red-400" />
              )) : (
                <p className="text-sm text-gray-400 text-center py-6">ไม่มีใบอนุญาตหมดอายุ</p>
              )}
            </div>
          </div>

          {/* Expiring Soon */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-50">
              <div className="flex items-center text-orange-600">
                <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 mr-2" />
                <h3 className="font-semibold text-gray-900 text-sm">หมดอายุใน 30 วัน</h3>
              </div>
              <span className="bg-orange-100 text-orange-700 text-xs font-bold px-2 py-0.5 rounded-full">{alerts.expiringSoon.length}</span>
            </div>
            <div className="p-4 space-y-2 max-h-64 overflow-y-auto">
              {alerts.expiringSoon.length > 0 ? alerts.expiringSoon.map(a => (
                <AgentRow key={a.id} agent={a} dateLabel="หมดอายุ" dateValue={a.licenseExpiryDate} statusColor="border-orange-400" />
              )) : (
                <p className="text-sm text-gray-400 text-center py-6">ไม่มีใบอนุญาตที่จะหมดใน 30 วัน</p>
              )}
            </div>
          </div>

          {/* Terminated Agents */}
          {alerts.terminatedAgents.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 lg:col-span-2">
              <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-50">
                <div className="flex items-center text-gray-600">
                  <SafeIcon icon={FiAlertCircle} className="w-4 h-4 mr-2" />
                  <h3 className="font-semibold text-gray-900 text-sm">สัญญาตัวแทนสิ้นผลบังคับ</h3>
                </div>
                <span className="bg-gray-100 text-gray-700 text-xs font-bold px-2 py-0.5 rounded-full">{alerts.terminatedAgents.length}</span>
              </div>
              <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-2 max-h-52 overflow-y-auto">
                {alerts.terminatedAgents.map(a => (
                  <AgentRow key={a.id} agent={a} dateLabel="หมดอายุ" dateValue={a.licenseExpiryDate} statusColor="border-gray-400" />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Recent Activities & System Health ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="px-5 pt-5 pb-3 border-b border-gray-50">
            <h3 className="font-semibold text-gray-900 text-sm">กิจกรรมล่าสุด</h3>
          </div>
          <div className="p-4 space-y-3 max-h-72 overflow-y-auto">
            {stats.recentActivities?.length > 0 ? stats.recentActivities.map((item) => {
              const style = ACTIVITY_ICONS[item.icon] || ACTIVITY_ICONS.system;
              return (
                <div key={item.id} className="flex items-start space-x-3">
                  <div className={`p-1.5 rounded-full ${style.bg} flex-shrink-0 mt-0.5`}>
                    <SafeIcon icon={style.icon} className={`w-3 h-3 ${style.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-800 leading-snug">{item.text}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{item.time}</p>
                  </div>
                </div>
              );
            }) : (
              <p className="text-sm text-gray-400 text-center py-4">ไม่มีกิจกรรม</p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="px-5 pt-5 pb-3 border-b border-gray-50">
            <h3 className="font-semibold text-gray-900 text-sm">สถานะระบบ</h3>
          </div>
          <div className="p-4 space-y-4">
            {[
              { label: 'ฐานข้อมูล', value: stats.systemHealth?.database },
              { label: 'API Services', value: stats.systemHealth?.api },
              { label: 'LINE Integration', value: stats.systemHealth?.lineIntegration },
            ].map(({ label, value }) => (
              <div key={label} className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{label}</span>
                <span className="flex items-center text-sm text-green-600 font-medium">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                  {value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Calendar Events ── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-5">
          <h3 className="font-semibold text-gray-900 mb-2 sm:mb-0">ปฏิทินกิจกรรมตัวแทน</h3>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
            className="block w-full sm:w-auto pl-3 pr-8 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          >
            {months.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Birthdays */}
          <div className="border border-gray-100 rounded-xl p-4 bg-pink-50/40">
            <div className="flex items-center mb-3 text-pink-600">
              <SafeIcon icon={FiGift} className="w-4 h-4 mr-2" />
              <h4 className="font-medium text-gray-900 text-sm">วันเกิดในเดือนนี้</h4>
              <span className="ml-auto text-xs bg-pink-100 text-pink-700 px-2 py-0.5 rounded-full font-bold">{events.birthdays.length}</span>
            </div>
            <div className="space-y-2 max-h-56 overflow-y-auto">
              {events.birthdays.length > 0 ? events.birthdays.map((agent) => (
                <div key={agent.id} className="flex items-center bg-white p-2.5 rounded-lg shadow-sm">
                  {agent.profileImage ? (
                    <img src={agent.profileImage} alt="" className="w-8 h-8 rounded-full object-cover mr-3" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-pink-100 flex items-center justify-center text-pink-500 mr-3 text-xs font-bold">
                      {agent.fullName?.charAt(0)}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{agent.fullName}</p>
                    <p className="text-xs text-gray-400">วันที่ {agent.day}</p>
                  </div>
                </div>
              )) : (
                <p className="text-sm text-gray-400 text-center py-4">ไม่มีวันเกิดในเดือนนี้</p>
              )}
            </div>
          </div>

          {/* Month License Expirations */}
          <div className="border border-gray-100 rounded-xl p-4 bg-orange-50/40">
            <div className="flex items-center mb-3 text-orange-600">
              <SafeIcon icon={FiAlertCircle} className="w-4 h-4 mr-2" />
              <h4 className="font-medium text-gray-900 text-sm">ใบอนุญาตหมดอายุในเดือนนี้</h4>
              <span className="ml-auto text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-bold">{events.expiringLicenses.length}</span>
            </div>
            <div className="space-y-2 max-h-56 overflow-y-auto">
              {events.expiringLicenses.length > 0 ? events.expiringLicenses.map((agent) => (
                <div key={agent.id} className="flex items-center bg-white p-2.5 rounded-lg shadow-sm border-l-4 border-orange-400">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{agent.fullName}</p>
                    <p className="text-xs text-gray-400">หมดอายุ: {agent.date}</p>
                  </div>
                </div>
              )) : (
                <p className="text-sm text-gray-400 text-center py-4">ไม่มีใบอนุญาตหมดอายุในเดือนนี้</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
